import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const getAuthToken = () => {
  const token = sessionStorage.getItem('loggedUser');
  const storedUser = JSON.parse(token);
  return storedUser?.result?.token || storedUser?.token || storedUser?.data?.token || storedUser?.user?.token;
};

const BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL || 'https://backend.mystore.education/api';

// Fetch main banners
export const fetchMainBanners = createAsyncThunk(
  'mainBanners/fetchMainBanners',
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }


      const response = await axios.get(`${BASE_URL}/banner`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar'
        }
      });


      if (response.data.status === 'success') {
        return {
          data: response.data.result || [],
          message: response.data.message || 'Main banners fetched successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch main banners');
      }
    } catch (error) {
      console.error('❌ Error fetching main banners:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Error fetching main banners');
    }
  }
);

// Add main banner
export const addMainBanner = createAsyncThunk(
  'mainBanners/addMainBanner',
  async (formData, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }


      const response = await axios.post(`${BASE_URL}/add_new_banner`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
          'Content-Type': 'multipart/form-data'
        }
      });


      if (response.data.status === 'success') {
        return {
          data: response.data.result,
          message: response.data.message || 'Main banner created successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to create main banner');
      }
    } catch (error) {
      console.error('❌ Error adding main banner:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Error creating main banner');
    }
  }
);

// Update main banner
export const updateMainBanner = createAsyncThunk(
  'mainBanners/updateMainBanner',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }

 

      const response = await axios.post(`${BASE_URL}/update_new_banner/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
          'Content-Type': 'multipart/form-data'
        }
      });


      if (response.data.status === 'success') {
        return {
          id: id,
          data: response.data.result,
          message: response.data.message || 'Main banner updated successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to update main banner');
      }
    } catch (error) {
      console.error('❌ Error updating main banner:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Error updating main banner');
    }
  }
);

// Update main banner status
export const updateMainBannerStatus = createAsyncThunk(
  'mainBanners/updateMainBannerStatus',
  async (id, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }

    
      const response = await axios.get(`${BASE_URL}/update_banner_status/${id}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar'
        }
      });


      if (response.data.status === 'success') {
        return {
          id: id,
          message: response.data.message || 'Main banner status updated successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to update main banner status');
      }
    } catch (error) {
      console.error('❌ Error updating main banner status:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Error updating main banner status');
    }
  }
);

// Delete main banner
export const deleteMainBanner = createAsyncThunk(
  'mainBanners/deleteMainBanner',
  async (id, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token found');
      }


      const response = await axios.delete(`${BASE_URL}/delete_banner/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar'
        }
      });


      if (response.data.status === 'success') {
        return {
          id: id,
          message: response.data.message || 'Main banner deleted successfully'
        };
      } else {
        throw new Error(response.data.message || 'Failed to delete main banner');
      }
    } catch (error) {
      console.error('❌ Error deleting main banner:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Error deleting main banner');
    }
  }
);

const initialState = {
  data: [],
  loading: false,
  error: null,
  success: false
};

const mainBannerSlice = createSlice({
  name: 'mainBanners',
  initialState,
  reducers: {
    clearMainBannerState: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
      state.success = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch main banners
      .addCase(fetchMainBanners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMainBanners.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.success = true;
      })
      .addCase(fetchMainBanners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Add main banner
      .addCase(addMainBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMainBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(addMainBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Update main banner
      .addCase(updateMainBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMainBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateMainBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Update main banner status
      .addCase(updateMainBannerStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMainBannerStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateMainBannerStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Delete main banner
      .addCase(deleteMainBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMainBanner.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(deleteMainBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  }
});

export const { clearMainBannerState } = mainBannerSlice.actions;
export default mainBannerSlice.reducer;
